/**
 * Modal que exibe o link de convite pra novo usuario cadastrar sua passkey.
 *
 * Ate termos provedor de email, o admin copia o link e envia manualmente
 * (o botao mailto: abre o cliente de email com corpo pre-preenchido).
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from "@payflow/ui";

export interface InviteLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** URL completa do convite, ex: https://app.cobanq.com.br/register-passkey?token=xxx */
  url: string;
  /** Nome do convidado — usado nos textos do modal. */
  userName: string;
  /** Email do convidado — usado no mailto:. */
  userEmail?: string;
  /** ISO date de expiracao — mostrado como hint no modal. */
  expiresAt?: string;
}

export function InviteLinkDialog({
  open,
  onOpenChange,
  url,
  userName,
  userEmail,
  expiresAt,
}: InviteLinkDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const expiresText = useMemo(() => {
    if (!expiresAt) return null;
    try {
      return new Date(expiresAt).toLocaleString(i18n.language || "pt-BR");
    } catch {
      return expiresAt;
    }
  }, [expiresAt, i18n.language]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ variant: "success", title: t("invite.copied") });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ variant: "error", title: t("invite.copy_error") });
    }
  };

  const mailtoHref = userEmail
    ? `mailto:${encodeURIComponent(userEmail)}` +
      `?subject=${encodeURIComponent(t("invite.email_subject"))}` +
      `&body=${encodeURIComponent(t("invite.email_body", { url }))}`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("invite.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("invite.message", { name: userName })}
          </p>

          <div className="rounded-lg border border-border bg-muted/50 p-3">
            <p className="break-all font-mono text-xs text-foreground">{url}</p>
          </div>

          {expiresText && (
            <p className="text-xs text-muted-foreground">
              {t("invite.expires_at", { date: expiresText })}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleCopy} variant={copied ? "outline" : "primary"}>
              {copied ? t("invite.copied") : t("invite.copy")}
            </Button>
            {mailtoHref && (
              <a
                href={mailtoHref}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-border bg-card px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                {t("invite.send_email")}
              </a>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
